/**
 * Pure mapping from (promo kind, claim state) to a next-action descriptor.
 * UI components consume this — they never compute action text themselves.
 *
 * Returned shape:
 *  - i18nKey: which translation key holds the "what to do next" sentence
 *  - params:  values to interpolate (bigint money, dates, multipliers)
 *  - cta:     i18n key for the button label, or null if no CTA (passive/done)
 *  - action:  semantic action — used by the dispatcher to wire the click
 *  - href:    where to navigate when action is 'wager' | 'deposit' | 'claim' nav
 */
import type { PromotionClaim } from './api.js';

export type PromoKind = 'welcome' | 'first_deposit' | 'reload' | 'cashback' | 'turnover_rebate' | 'daily_checkin';

export type NextAction = 'claim' | 'deposit' | 'wager' | 'passive' | 'done';

export interface NextActionResult {
  i18nKey: string | null;
  params: Record<string, unknown>;
  cta: string | null;
  action: NextAction;
  href: string | null;
}

export interface NextActionInput {
  kind: PromoKind;
  claim: PromotionClaim | null;
  dayAmount?: bigint;
  nextDayAmount?: bigint;
  /**
   * daily_checkin only: whether the member has already checked in on the
   * current Asia/Bangkok day, as answered by the server from the same rows the
   * claim gate reads.
   *
   * It is not derivable from `claim`. A check-in claim stays `active` until its
   * turnover clears, so an untouched claim from days ago looks identical to
   * today's; and once the turnover does clear the row goes `completed` and
   * leaves the active-claims list entirely, so today's claim looks like no
   * claim at all. This card used to answer the question itself by comparing the
   * claim row's UTC calendar date, which was wrong in both directions and wrong
   * again for the first seven hours of every Bangkok day (#366).
   *
   * `undefined` means the answer has not arrived yet — the card offers no
   * action rather than guessing which of two amounts to show.
   */
  claimedToday?: boolean;
  /**
   * Formats a minor-unit amount for display (e.g. `formatMoney`). Money params
   * are passed through this before interpolation so the CTA strings render
   * "฿38.00" instead of the raw minor units. Defaults to identity (returns the
   * bigint unchanged) so callers/tests that don't supply it keep the raw value.
   */
  money?: (minor: bigint) => string;
}

function bigIntish(v: bigint | number | string | null | undefined): bigint {
  if (v == null) return 0n;
  if (typeof v === 'bigint') return v;
  return BigInt(v);
}

function wagerRemaining(c: PromotionClaim): bigint {
  const need = bigIntish(c.turnoverRequired);
  const done = bigIntish(c.turnoverAchieved);
  return done >= need ? 0n : need - done;
}

/**
 * Which of the 3 stepper nodes is active (4 = all done). The deposit-shaped
 * kinds (first_deposit / reload) read Deposit → Auto-credit → Wager, so the
 * wager step is 3; welcome reads Claim → Wager → Convert, so its wager step is
 * 2. A pending reservation has turnoverRequired = 0 (the real target is set
 * only when the deposit credits), so `met` must require a positive requirement
 * — otherwise 0 >= 0 falsely marks the deposit/credit steps done before the
 * player has deposited.
 */
export function currentStepFor(kind: PromoKind, claim: PromotionClaim | null): 1 | 2 | 3 | 4 {
  if (!claim) return 1;
  if (claim.status === 'completed' || claim.status === 'released') return 4;
  const req = bigIntish(claim.turnoverRequired);
  const met = req > 0n && bigIntish(claim.turnoverAchieved) >= req;
  if (kind === 'first_deposit' || kind === 'reload') {
    if (claim.status === 'pending') return 1;
    return met ? 4 : 3;
  }
  return met ? 3 : 2;
}

/** No line, no button — the card renders nothing in its action slot. */
const SILENT: NextActionResult = {
  i18nKey: null,
  params: {},
  cta: null,
  action: 'passive',
  href: null,
};

export function nextActionFor(input: NextActionInput): NextActionResult {
  const { kind, claim } = input;
  const money = input.money ?? ((v: bigint) => v as unknown as string);

  // ── daily_checkin: the server's Bangkok answer decides, not the claim row ──
  //
  // Handled ahead of every claim-shaped branch below because none of them can
  // answer it. The claim row that matters may be absent (today's claim finished
  // its turnover and left the active list) or stale (an untouched claim from an
  // earlier day is still 'active'), and both states used to read as the
  // opposite of the truth: an amount two schedule days ahead of what the ledger
  // would pay, or a live Claim button offering money already banked.
  if (kind === 'daily_checkin') {
    if (input.claimedToday === undefined) {
      // Not yet known. Every branch here names a money amount, and there is no
      // amount that is right in both states, so the card says nothing.
      return SILENT;
    }
    const amount = input.claimedToday ? input.nextDayAmount : input.dayAmount;
    // Nothing payable, so nothing to say. A promotion whose schedule is empty
    // (or whose day pays zero) is one `calcReward` refuses — PROMO-050 for no
    // schedule, PROMO-051 for a zero grant — so the states below would be a
    // "Come back tomorrow for ฿0.00" line, or worse, an enabled Claim button
    // shaped like money that cannot succeed on any press.
    if (amount === undefined || amount <= 0n) return SILENT;
    if (input.claimedToday) {
      return {
        i18nKey: 'card.daily_checkin.active.backTomorrow',
        params: { amount: money(amount) },
        cta: null,
        action: 'passive',
        href: null,
      };
    }
    return {
      i18nKey: null,
      params: { amount: money(amount) },
      cta: 'card.daily_checkin.available.cta',
      action: 'claim',
      href: null,
    };
  }

  // ── No claim yet (available/unclaimed state) ──
  if (!claim) {
    switch (kind) {
      case 'welcome':
        return {
          i18nKey: null,
          params: {},
          cta: 'card.welcome.available.cta',
          action: 'claim',
          href: null,
        };
      case 'first_deposit':
      case 'reload':
        // Opt-in: clicking the CTA reserves a pending claim via POST /claim.
        // The deposit page is the player's next destination, but the claim
        // itself is what enrolls them — auto-claim only fires on a deposit
        // when a pending reservation exists.
        return {
          i18nKey: null,
          params: {},
          cta: `card.${kind}.available.cta`,
          action: 'claim',
          href: null,
        };
      case 'cashback':
      case 'turnover_rebate':
        // Manual claim per period — player taps to receive the accrued rebate.
        return { i18nKey: null, params: {}, cta: 'card.rebate.available.cta', action: 'claim', href: null };
    }
  }

  // ── Claim exists (pending/active/completed state) ──
  const status = claim.status ?? 'pending';

  // Pending reservation for first_deposit / reload — player opted in and
  // needs to deposit before the bonus credits. CTA goes to deposit page.
  if (status === 'pending' && (kind === 'first_deposit' || kind === 'reload')) {
    return {
      i18nKey: 'card.deposit.pending.nextAction',
      params: {},
      cta: 'card.deposit.pending.cta',
      action: 'deposit',
      href: '/wallet/deposit',
    };
  }

  // Terminal statuses: no more action — except a converted bonus, where the
  // money is now withdrawable cash, so point the player at the Withdraw page.
  if (
    status === 'completed' ||
    status === 'released' ||
    status === 'forfeited' ||
    status === 'rejected'
  ) {
    if (
      (status === 'completed' || status === 'released') &&
      (kind === 'welcome' || kind === 'first_deposit' || kind === 'reload')
    ) {
      return { i18nKey: null, params: {}, cta: 'card.bonus.withdraw.cta', action: 'done', href: '/wallet/withdraw' };
    }
    // A completed rebate claim is NOT withdrawable cash — the credit is still
    // sitting in the cashback pool and the player has to press Convert. The
    // bonus branch above would have sent them to Withdraw, where the money is
    // not.
    if (
      status === 'completed' &&
      (kind === 'cashback' || kind === 'turnover_rebate') &&
      bigIntish(claim.turnoverRequired) > 0n
    ) {
      return { i18nKey: null, params: {}, cta: 'card.cashback.unlocked.cta', action: 'done', href: '/wallet' };
    }
    return { i18nKey: null, params: {}, cta: null, action: 'done', href: null };
  }

  // Compute wager remaining for active claims
  const remaining = wagerRemaining(claim);

  // Active states: show next action based on kind
  switch (kind) {
    case 'welcome':
    case 'first_deposit':
    case 'reload':
      // Turnover met but the claim hasn't flipped to 'completed' yet (the
      // convert saga is async) — the bonus is effectively unlocked, so send
      // the player to Withdraw rather than telling them to keep wagering.
      if (remaining === 0n) {
        return { i18nKey: null, params: {}, cta: 'card.bonus.withdraw.cta', action: 'done', href: '/wallet/withdraw' };
      }
      return {
        i18nKey: `card.${kind}.active.nextAction`,
        params: { amount: money(remaining) },
        cta: `card.${kind}.active.cta`,
        action: 'wager',
        href: '/casino',
      };
    case 'cashback':
    case 'turnover_rebate': {
      // Both rebate kinds, one answer, because the money path stopped telling
      // them apart: `claimRebate` books either with the promotion's
      // multiplier, and the claim only reaches 'active' when that multiplier
      // put a real requirement on the credit. This branch was dead while every
      // rebate claim went pending → completed on the wallet ACK.
      //
      // A 'pending' one is still waiting for that ACK — the credit has not
      // landed, nobody can wager against it, and naming an amount would be
      // naming money the player cannot see yet.
      if (status !== 'active') return SILENT;
      // Turnover met, claim not yet flipped (the accrual consumer commits a
      // moment later). The credit is convertible NOW, so sending the player
      // back to the casino to keep wagering would be wrong twice over.
      if (remaining === 0n) {
        return {
          i18nKey: null,
          params: {},
          cta: 'card.cashback.unlocked.cta',
          action: 'done',
          href: '/wallet',
        };
      }
      return {
        i18nKey: 'card.cashback.credited.nextAction',
        params: { amount: money(remaining), credited: money(bigIntish(claim.grantedAmount)) },
        cta: 'card.cashback.credited.cta',
        action: 'wager',
        href: '/casino',
      };
    }
  }
}
